<template>
  <v-container fluid>
    <v-main class="remove-padding">
      <v-card class="my-0 py-4 px-4" style="width: 100%">
        <!-- 토글 버튼 (ArtistCup / EngineerCup) -->
        <div class="mb-4">
          <v-btn
            :outlined="selectedCup !== 'artist'"
            :color="selectedCup === 'artist' ? 'primary' : 'grey darken-1'"
            class="mr-2"
            @click="selectedCup = 'artist'"
          >
            예술가
          </v-btn>
          <v-btn
            :outlined="selectedCup !== 'engineer'"
            :color="selectedCup === 'engineer' ? 'primary' : 'grey darken-1'"
            @click="selectedCup = 'engineer'"
          >
            기술자
          </v-btn>
        </div>

        <!-- ArtistCup 랭킹 -->
        <div v-if="selectedCup === 'artist'">
          <v-toolbar flat color="white">
            <v-toolbar-title>
              최악의 예술가 랭킹 (참여: {{ artistParticipantCount }}명)
            </v-toolbar-title>
          </v-toolbar>

          <v-data-table
            class="fixed-table elevation-1"
            style="width: 100%"
            :headers="computedHeaders"
            :items="rankingArtist"
            :items-per-page="10"
          >
            <!-- 이미지 컬럼 (PC 전용) -->
            <template v-slot:[`item.image`]="{ item }">
              <v-img v-if="item.image" :src="item.image" max-height="300" />
            </template>

            <!-- name 컬럼 -->
            <template v-slot:[`item.name`]="{ item }">
              <div class="card-title-normal">
                {{ item.name }}
              </div>
            </template>

            <!-- 우승 비율(%) -->
            <template v-slot:[`item.finalWinRatePercent`]="{ item }">
              <div>{{ item.finalWinRatePercent.toFixed(2) }}%</div>
            </template>

            <!-- 승률(%) -->
            <template v-slot:[`item.winRatePercent`]="{ item }">
              <div>{{ item.winRatePercent.toFixed(2) }}%</div>
            </template>

            <!-- 설명 열 -->
            <template v-slot:[`item.desc`]="{ item }">
              <div class="multi-line-text">
                {{ item.desc }}
              </div>
            </template>
          </v-data-table>
        </div>

        <!-- EngineerCup 랭킹 -->
        <div v-else>
          <v-toolbar flat color="white">
            <v-toolbar-title>
              최악의 기술자 랭킹 (참여: {{ engineerParticipantCount }}명)
            </v-toolbar-title>
          </v-toolbar>

          <v-data-table
            class="fixed-table elevation-1"
            style="width: 100%"
            :headers="computedHeaders"
            :items="rankingEngineer"
            :items-per-page="10"
          >
            <!-- 이미지 컬럼 (PC 전용) -->
            <template v-slot:[`item.image`]="{ item }">
              <v-img v-if="item.image" :src="item.image" max-height="300" />
            </template>

            <!-- name 컬럼 -->
            <template v-slot:[`item.name`]="{ item }">
              <div class="card-title-normal">
                {{ item.name }}
              </div>
            </template>

            <!-- 우승 비율(%) -->
            <template v-slot:[`item.finalWinRatePercent`]="{ item }">
              <div>{{ item.finalWinRatePercent.toFixed(2) }}%</div>
            </template>

            <!-- 승률(%) -->
            <template v-slot:[`item.winRatePercent`]="{ item }">
              <div>{{ item.winRatePercent.toFixed(2) }}%</div>
            </template>

            <!-- 설명 열 -->
            <template v-slot:[`item.desc`]="{ item }">
              <div class="multi-line-text">
                {{ item.desc }}
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-main>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import { artistData } from "@/data/artistCupData.js";
import { engineerData } from "@/data/engineerCupData.js";

/* PC / 모바일 헤더 */
const headersPc = [
  { title: "랭킹", key: "rank", width: 30, class: "col-rank" },
  { title: "이미지", key: "image", width: 120, class: "col-image" },
  { title: "이름", key: "name", width: 120, class: "col-name" },
  {
    // ★ 실제 줄바꿈 문자를 백틱(템플릿 리터럴)으로 삽입
    title: `우승 비율
(최종 우승 횟수/ 전체 게임 수)`,
    key: "finalWinRatePercent",
    width: 80,
    class: "col-final",
  },
  {
    // 여기도 \n 넣고 싶다면 동일하게
    // title: `승률
    // (승리 횟수/ 전체 1:1 대결수)`,
    // 혹은
    title: "승률\n(승리 횟수/ 전체 1:1 대결수)",
    key: "winRatePercent",
    width: 80,
    class: "col-win",
  },
  { title: "설명", key: "desc", width: 300, class: "col-desc" },
];

const headersMobile = [
  { title: "랭킹", key: "rank", class: "col-rank" },
  { title: "이름", key: "name", class: "col-name" },
  { title: "우승 비율(%)", key: "finalWinRatePercent", class: "col-final" },
];

const items = ref([]); // API로부터 게임 결과(배열)
const selectedCup = ref("artist");
const isMobile = ref(false);

function checkMobile() {
  isMobile.value = window.innerWidth <= 600;
}

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
  fetchData();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkMobile);
});

/** '\n' 및 '+' 치환 */
function decodeString(str) {
  if (!str) return "";
  let decoded = str.replace(/\\n/g, "\n");
  decoded = decoded.replace(/\+/g, " ");
  return decoded;
}

/** (★) API 호출 */
async function fetchData() {
  try {
    const resp = await axios.get(
      "https://script.google.com/macros/s/AKfycbwZddH4phffDw-C_3p2rM-F94pzK8EMYVe5Sm1w8MCZ0PdfqJVTSMTzgMEjj6TLz__O1A/exec"
    );
    items.value = resp.data; // 배열 그대로 저장
  } catch (err) {
    console.error("fetch failed:", err);
  }
}

// PC/모바일에 따라 다른 headers 반환
const computedHeaders = computed(() => {
  return isMobile.value ? headersMobile : headersPc;
});

/** 예술가 목록(이미지/desc) 매핑 */
const artistMap = {};
artistData.forEach((obj) => {
  artistMap[obj.name] = obj;
});

/** 기술자 목록(이미지/desc) 매핑 */
const engineerMap = {};
engineerData.forEach((obj) => {
  engineerMap[obj.name] = obj;
});

/** (★) 예술가 참여자 수(= ArtistCup인 게임 개수) */
const artistParticipantCount = computed(() => {
  // cup_id가 'ArtistCup'인 레코드 개수
  return items.value.filter((r) => r.cup_id === "ArtistCup").length;
});

/** (★) 기술자 참여자 수(= EngineerCup인 게임 개수) */
const engineerParticipantCount = computed(() => {
  // cup_id가 'EngineerCup'인 레코드 개수
  return items.value.filter((r) => r.cup_id === "EngineerCup").length;
});

/** (★) ArtistCup 랭킹 계산 로직 */
const rankingArtist = computed(() => {
  const filtered = items.value.filter((r) => r.cup_id === "ArtistCup");
  if (!filtered.length) return [];

  const totalGames = filtered.length;
  const statsMap = {};

  filtered.forEach((game) => {
    const fw = decodeString(game.finalWinner);
    if (fw) {
      statsMap[fw] ??= {
        name: fw,
        finalWins: 0,
        total1v1Wins: 0,
        total1v1Matches: 0,
      };
      statsMap[fw].finalWins++;
    }

    let matches = [];
    try {
      if (game.matchResults) {
        matches = JSON.parse(game.matchResults);
      }
    } catch (e) {
      console.error("parse err(artist)", e);
    }

    matches.forEach((m) => {
      const cA = decodeString(m.candidateA);
      const cB = decodeString(m.candidateB);
      const chosen = decodeString(m.chosen);

      [cA, cB].forEach((cand) => {
        if (!cand) return;
        statsMap[cand] ??= {
          name: cand,
          finalWins: 0,
          total1v1Wins: 0,
          total1v1Matches: 0,
        };
        statsMap[cand].total1v1Matches++;
      });

      if (chosen && statsMap[chosen]) {
        statsMap[chosen].total1v1Wins++;
      }
    });
  });

  // 각 후보별 우승 비율(최종 우승 횟수 / 전체 게임 수) 및 1:1 승률 계산
  const arr = Object.values(statsMap).map((s) => {
    const finalWinRate = totalGames ? s.finalWins / totalGames : 0;
    const winRate = s.total1v1Matches ? s.total1v1Wins / s.total1v1Matches : 0;
    const info = artistMap[s.name] || {};

    return {
      ...s,
      finalWinRatePercent: finalWinRate * 100,
      winRatePercent: winRate * 100,
      image: info.img || null,
      desc: decodeString(info.desc) || "",
    };
  });

  // 우승 비율 desc, 동률이면 승률 desc
  arr.sort((a, b) => {
    if (b.finalWinRatePercent !== a.finalWinRatePercent) {
      return b.finalWinRatePercent - a.finalWinRatePercent;
    }
    return b.winRatePercent - a.winRatePercent;
  });

  // 순위 부여
  arr.forEach((item, idx) => {
    item.rank = idx + 1;
  });

  return arr;
});

/** (★) EngineerCup 랭킹 계산 로직 */
const rankingEngineer = computed(() => {
  const filtered = items.value.filter((r) => r.cup_id === "EngineerCup");
  if (!filtered.length) return [];

  const totalGames = filtered.length;
  const statsMap = {};

  filtered.forEach((game) => {
    const fw = decodeString(game.finalWinner);
    if (fw) {
      statsMap[fw] ??= {
        name: fw,
        finalWins: 0,
        total1v1Wins: 0,
        total1v1Matches: 0,
      };
      statsMap[fw].finalWins++;
    }

    let matches = [];
    try {
      if (game.matchResults) {
        matches = JSON.parse(game.matchResults);
      }
    } catch (e) {
      console.error("parse err(engineer)", e);
    }

    matches.forEach((m) => {
      const cA = decodeString(m.candidateA);
      const cB = decodeString(m.candidateB);
      const chosen = decodeString(m.chosen);

      [cA, cB].forEach((cand) => {
        if (!cand) return;
        statsMap[cand] ??= {
          name: cand,
          finalWins: 0,
          total1v1Wins: 0,
          total1v1Matches: 0,
        };
        statsMap[cand].total1v1Matches++;
      });

      if (chosen && statsMap[chosen]) {
        statsMap[chosen].total1v1Wins++;
      }
    });
  });

  const arr = Object.values(statsMap).map((s) => {
    const finalWinRate = totalGames ? s.finalWins / totalGames : 0;
    const winRate = s.total1v1Matches ? s.total1v1Wins / s.total1v1Matches : 0;
    const info = engineerMap[s.name] || {};

    return {
      ...s,
      finalWinRatePercent: finalWinRate * 100,
      winRatePercent: winRate * 100,
      image: info.img || null,
      desc: decodeString(info.desc) || "",
    };
  });

  // 우승 비율 desc, 동률이면 승률 desc
  arr.sort((a, b) => {
    if (b.finalWinRatePercent !== a.finalWinRatePercent) {
      return b.finalWinRatePercent - a.finalWinRatePercent;
    }
    return b.winRatePercent - a.winRatePercent;
  });

  arr.forEach((item, idx) => {
    item.rank = idx + 1;
  });

  return arr;
});
</script>

<style scoped>
.multi-line-text {
  white-space: pre-wrap; /* \n 처리 */
  word-break: break-word;
  max-height: 120px;
  overflow-y: auto;
}

/* 고정 레이아웃으로 열 너비 적용 */
.fixed-table table {
  table-layout: fixed;
  width: 100%;
}

/* PC 열 너비 */
th.col-rank {
  width: 30px;
}
th.col-image {
  width: 120px;
}
th.col-name {
  width: 120px;
}

/* (핵심) 우승 비율 / 승률 컬럼에 줄바꿈 적용 */
th.col-final,
th.col-win {
  white-space: pre-wrap !important; /* ★ 중요: 실제 줄바꿈 반영 */
  font-size: 16px;
  line-height: 1.4;
  font-weight: bold;
  overflow: visible;
  text-overflow: unset;
  word-wrap: break-word;
  word-break: break-all;
  width: 80px;
}

th.col-desc {
  width: 300px;
}

/* 모바일 열 너비 */
@media (max-width: 600px) {
  .fixed-table table {
    table-layout: auto !important;
  }
}

/* 카드 제목 - 여러 줄 가능, '...'로 생략되지 않도록 */
.card-title-normal {
  font-size: 16px;
  line-height: 1.4;
  font-weight: bold;
  white-space: pre-wrap;
  overflow: visible;
  text-overflow: unset;
  word-wrap: break-word;
  word-break: break-all;
}
.remove-padding {
  /* v-main이 잡는 기본 상단 padding을 없앰 */
  padding-top: 0 !important;
}
</style>
